import React, { useMemo, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { AppButton } from '../../components/AppButton';
import { AutoGrowTextInput } from '../../components/AutoGrowTextInput';
import { AppDateField } from '../../components/AppDateField';
import { AppDialogFrame } from '../../components/AppDialogFrame';
import { AppInput } from '../../components/AppInput';
import { ProjectMemberPicker } from '../../components/ProjectMemberPicker';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { useTimeLogEmployee } from '../../hooks/useTimeLogEmployee';
import { addTaskTimeLog, createProjectTask } from '../../services/projectService';
import type { ProjectBoardColumn, ProjectMember, ProjectTask } from '../../types/project';
import { todayBusinessCalendarDate } from '../../utils/dateTime';
import { projectErrorMessage } from '../../utils/projectErrors';
import {
  expandSubtaskDraft,
  formatDurationInput,
  isMultiPointPaste,
  isSubtaskList,
  nextTaskDraftKey,
  validateCreateTaskForm,
  type TaskFormFieldErrors,
  type TaskSubtaskDraft,
} from '../../utils/projectTaskForm';
import { runOperation } from '../../utils/operationFeedback';
import { finishEditingProps } from '../../utils/textInputEvents';
import {
  resolveCompletedDate,
  type CompletedOnChoice,
} from '../../utils/taskCompletionDate';
import { timeLogDurationMinutes, timeRangeError } from '../../utils/taskTimeLog';
import { TimeEntryFields } from './TimeEntryFields';

type Props = {
  visible: boolean;
  projectId: number;
  column: ProjectBoardColumn | null;
  members: ProjectMember[];
  onClose: () => void;
  onCreated: () => void;
};

export function CreateTaskModal({
  visible,
  projectId,
  column,
  members,
  onClose,
  onCreated,
}: Props) {
  const palette = useAppColors();
  const { width: windowWidth } = useWindowDimensions();
  // The member field sits beside the subtask text; narrower on phones.
  const memberFieldWidth = windowWidth < 600 ? 120 : 180;
  const themed = useMemo(
    () => ({
      title: { ...typography.sectionTitle, color: palette.textPrimary, flex: 1 },
      close: { ...typography.body, color: palette.textSecondary, fontWeight: '700' as const },
      section: {
        ...typography.caption,
        color: palette.textPrimary,
        fontWeight: '700' as const,
        flex: 1,
      },
      label: {
        ...typography.caption,
        color: palette.textSecondary,
        fontWeight: '600' as const,
      },
      icon: { ...typography.body, color: palette.primary, fontWeight: '700' as const },
      error: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  const [taskName, setTaskName] = useState('');
  const [assignedEmployeeIds, setAssignedEmployeeIds] = useState<number[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [timeEstimate, setTimeEstimate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [completedOn, setCompletedOn] = useState<CompletedOnChoice>('today');
  const [customCompletedDate, setCustomCompletedDate] = useState(
    () => todayBusinessCalendarDate(),
  );
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [subtasks, setSubtasks] = useState<TaskSubtaskDraft[]>([]);
  const [fieldErrors, setFieldErrors] = useState<TaskFormFieldErrors>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function reset() {
    setTaskName('');
    setAssignedEmployeeIds([]);
    setDueDate('');
    setTimeEstimate('');
    setStartTime('');
    setEndTime('');
    setCompletedOn('today');
    setCustomCompletedDate(todayBusinessCalendarDate());
    setDescriptionOpen(false);
    setDescription('');
    setSubtasks([]);
    setFieldErrors({});
    setError('');
    setBusy(false);
  }

  function close() {
    if (busy) return;
    reset();
    onClose();
  }

  const requiresDetails = column?.requiresTaskDetails === true;
  const requireCompletedDate = column?.isCompletedColumn === true;
  // A task created already done records its time as one From / To entry.
  const selectedAssignees = useMemo(
    () => members.filter(member => assignedEmployeeIds.includes(member.employeeId)),
    [assignedEmployeeIds, members],
  );
  const timeLogEmployee = useTimeLogEmployee({ assignees: selectedAssignees, members });

  function addSubtask(afterKey?: string) {
    const next: TaskSubtaskDraft = {
      key: nextTaskDraftKey('subtask'),
      name: '',
      assignedEmployeeIds: [],
    };
    setSubtasks(current => {
      if (!afterKey) return [...current, next];
      const index = current.findIndex(item => item.key === afterKey);
      if (index < 0) return [...current, next];
      const copy = [...current];
      copy.splice(index + 1, 0, next);
      return copy;
    });
  }

  function changeSubtaskName(item: TaskSubtaskDraft, value: string) {
    // A pasted list becomes one row per point straight away.
    setSubtasks(current =>
      isMultiPointPaste(item.name, value)
        ? expandSubtaskDraft(current, item.key, value)
        : current.map(row => (row.key === item.key ? { ...row, name: value } : row)),
    );
  }

  function finishSubtaskName(key: string) {
    // Typed lines (Enter between points) are split once the row loses focus.
    setSubtasks(current => {
      const row = current.find(item => item.key === key);
      return row && isSubtaskList(row.name)
        ? expandSubtaskDraft(current, key, row.name)
        : current;
    });
  }

  async function save() {
    if (busy || !column?.projectColumnId) return;
    const result = validateCreateTaskForm({
      taskName,
      assignedEmployeeIds,
      dueDate,
      timeEstimate,
      // Only a task created already done needs its spent time up front.
      ...(requireCompletedDate
        ? {
            timeSpent:
              formatDurationInput(timeLogDurationMinutes(startTime, endTime)) || '00:00',
          }
        : {}),
      description,
      descriptionOpen,
      subtasks,
      requiresTaskDetails: requiresDetails,
      requireCompletedDate,
      completedOn,
      customCompletedDate,
      columnName: column.columnName,
    });
    const timeError = requireCompletedDate
      ? timeRangeError(startTime, endTime) ?? timeLogEmployee.missingMessage ?? undefined
      : undefined;
    setFieldErrors({ ...result.fieldErrors, timeSpent: timeError });
    if (!result.payload || timeError) {
      return;
    }
    const payload = result.payload;
    const projectColumnId = column.projectColumnId;
    const logDate =
      payload.completedDate ?? resolveCompletedDate(completedOn, customCompletedDate);
    // Set once the task exists, so a failed time entry is not reported as a
    // failed task (and retrying does not create the task twice).
    const saved: { task: ProjectTask | null } = { task: null };
    setBusy(true);
    setError('');
    try {
      await runOperation(
        'add',
        async () => {
          saved.task = await createProjectTask(projectId, {
            ...payload,
            projectColumnId,
          });
          if (requireCompletedDate && logDate) {
            await addTaskTimeLog(projectId, saved.task.taskId, {
              date: logDate,
              startTime,
              endTime,
              source: 'MANUAL',
              ...(timeLogEmployee.requestEmployeeId != null
                ? { employeeId: timeLogEmployee.requestEmployeeId }
                : {}),
            });
          }
        },
        {
          failureDetail: caught =>
            saved.task
              ? `The task was created, but its time entry was not saved: ${projectErrorMessage(
                  caught,
                  'Unable to add the time log.',
                )} Add it from the task's Time spent.`
              : projectErrorMessage(caught, 'Unable to save the task.'),
        },
      );
      reset();
      onCreated();
    } catch (caught) {
      if (saved.task) {
        reset();
        onCreated();
        return;
      }
      setError(projectErrorMessage(caught, 'Unable to save the task.'));
    } finally {
      setBusy(false);
    }
  }

  if (!visible || !column) return null;

  return (
    <AppDialogFrame visible onRequestClose={close}>
      <View style={[styles.flex, { backgroundColor: palette.background }]}>
        <View style={styles.header}>
          <Text style={themed.title}>Create Task</Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={close}
            hitSlop={8}
          >
            <Text style={themed.close}>X</Text>
          </Pressable>
        </View>
        <ScrollView
          contentContainerStyle={styles.body}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={[styles.columnHint, { color: palette.textSecondary }]}>
            Column: {column.columnName}
          </Text>

          <AppInput
            label="Task Name"
            value={taskName}
            onChangeText={setTaskName}
            placeholder="Enter task name"
            error={fieldErrors.taskName}
          />

          {requireCompletedDate ? (
            <>
              <Text style={themed.label}>Completed on *</Text>
              <View style={styles.choiceRow}>
                {(
                  [
                    { value: 'yesterday', label: 'Yesterday' },
                    { value: 'today', label: 'Today' },
                    { value: 'custom', label: 'Other date' },
                  ] as const
                ).map(choice => {
                  const selected = completedOn === choice.value;
                  return (
                    <Pressable
                      key={choice.value}
                      accessibilityRole="button"
                      accessibilityLabel={choice.label}
                      accessibilityState={{ selected }}
                      onPress={() => {
                        setCompletedOn(choice.value);
                        if (choice.value === 'custom') {
                          setCustomCompletedDate('');
                        }
                        setFieldErrors(current => ({
                          ...current,
                          completedDate: undefined,
                        }));
                      }}
                      style={[
                        styles.choice,
                        {
                          borderColor: selected ? palette.primary : palette.border,
                          backgroundColor: selected
                            ? palette.primary
                            : palette.surface,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.choiceText,
                          {
                            color: selected
                              ? palette.onPrimary
                              : palette.textPrimary,
                          },
                        ]}
                      >
                        {choice.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
              {completedOn === 'custom' ? (
                <AppDateField
                  label="Completion date *"
                  value={customCompletedDate}
                  onChange={value => {
                    setCustomCompletedDate(value);
                    setFieldErrors(current => ({
                      ...current,
                      completedDate: undefined,
                    }));
                  }}
                  placeholder="Select date"
                  error={fieldErrors.completedDate}
                  maximumDate={new Date()}
                />
              ) : fieldErrors.completedDate ? (
                <Text accessibilityRole="alert" style={themed.error}>
                  {fieldErrors.completedDate}
                </Text>
              ) : null}
            </>
          ) : null}

          <View style={styles.infoRow}>
            <View style={styles.infoField}>
              <ProjectMemberPicker
                label={requiresDetails ? 'Members *' : 'Members'}
                members={members}
                selectedIds={assignedEmployeeIds}
                onChange={setAssignedEmployeeIds}
                error={fieldErrors.member}
              />
            </View>
            <View style={styles.infoField}>
              <AppDateField
                label={requiresDetails ? 'Due Date *' : 'Due Date'}
                value={dueDate}
                onChange={setDueDate}
                placeholder="Select date"
                error={fieldErrors.dueDate}
              />
            </View>
            <View style={styles.infoField}>
              <AppInput
                label={requiresDetails ? 'Time Estimate *' : 'Time Estimate'}
                value={timeEstimate}
                onChangeText={setTimeEstimate}
                placeholder="00:00"
                error={fieldErrors.timeEstimate}
                autoCapitalize="none"
                keyboardType="numbers-and-punctuation"
              />
            </View>
          </View>
          {requireCompletedDate ? (
            <TimeEntryFields
              startTime={startTime}
              endTime={endTime}
              onChangeStart={value => {
                setStartTime(value);
                setFieldErrors(current => ({ ...current, timeSpent: undefined }));
              }}
              onChangeEnd={value => {
                setEndTime(value);
                setFieldErrors(current => ({ ...current, timeSpent: undefined }));
              }}
              error={fieldErrors.timeSpent}
              required
              employee={timeLogEmployee}
            />
          ) : null}

          <View style={styles.sectionHeader}>
            <Text style={themed.section}>Description</Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={
                descriptionOpen ? 'Hide description' : 'Add description'
              }
              onPress={() => {
                if (descriptionOpen) {
                  setDescription('');
                  setDescriptionOpen(false);
                  return;
                }
                setDescriptionOpen(true);
              }}
              hitSlop={8}
            >
              <Text style={themed.icon}>{descriptionOpen ? '−' : '+'}</Text>
            </Pressable>
          </View>
          {descriptionOpen ? (
            <TextInput
              accessibilityLabel="Description"
              value={description}
              onChangeText={setDescription}
              placeholder="Enter task description here..."
              placeholderTextColor={palette.textMuted}
              multiline
              textAlignVertical="top"
              style={[
                styles.description,
                {
                  borderColor: palette.border,
                  backgroundColor: palette.surface,
                  color: palette.textPrimary,
                },
              ]}
            />
          ) : null}

          <View style={styles.sectionHeader}>
            <Text style={themed.section}>Subtasks</Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Add Subtask"
              onPress={() => addSubtask()}
              hitSlop={8}
            >
              <Text style={themed.icon}>+</Text>
            </Pressable>
          </View>
          {subtasks.map(item => (
            <View key={item.key} testID="subtask-draft-row" style={styles.subtaskRow}>
              <AutoGrowTextInput
                accessibilityLabel="Subtask name"
                value={item.name}
                onChangeText={value => changeSubtaskName(item, value)}
                {...finishEditingProps(() => finishSubtaskName(item.key))}
                placeholder="Subtask name (paste a list to add several)"
                placeholderTextColor={palette.textMuted}
                minHeight={44}
                style={[
                  styles.subtaskInput,
                  {
                    borderColor: palette.border,
                    backgroundColor: palette.surface,
                    color: palette.textPrimary,
                  },
                ]}
              />
              <View style={{ width: memberFieldWidth }}>
                <ProjectMemberPicker
                  label="Subtask members"
                  showLabel={false}
                  members={members}
                  selectedIds={item.assignedEmployeeIds}
                  onChange={ids =>
                    setSubtasks(current =>
                      current.map(row =>
                        row.key === item.key
                          ? { ...row, assignedEmployeeIds: ids }
                          : row,
                      ),
                    )
                  }
                  accessibilityLabel={`Assign members to ${item.name || 'subtask'}`}
                />
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Add Subtask"
                onPress={() => addSubtask(item.key)}
                hitSlop={8}
                style={styles.subtaskIcon}
              >
                <Text style={themed.icon}>+</Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Remove subtask"
                onPress={() =>
                  setSubtasks(current => current.filter(row => row.key !== item.key))
                }
                hitSlop={8}
                style={styles.subtaskIcon}
              >
                <Text style={[themed.icon, { color: palette.error }]}>−</Text>
              </Pressable>
            </View>
          ))}
          {fieldErrors.subtasks ? (
            <Text style={themed.error}>{fieldErrors.subtasks}</Text>
          ) : null}

          {error ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {error}
            </Text>
          ) : null}

          <View style={styles.actions}>
            <AppButton title="Cancel" variant="secondary" onPress={close} />
            <AppButton title="Save" onPress={save} loading={busy} />
          </View>
        </ScrollView>
      </View>
    </AppDialogFrame>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xl,
    paddingBottom: spacing.md,
    gap: spacing.md,
  },
  body: {
    paddingHorizontal: spacing.xl,
    paddingBottom: spacing.xxl,
    gap: spacing.md,
  },
  columnHint: { ...typography.caption },
  choiceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  choice: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  choiceText: {
    ...typography.caption,
    fontWeight: '700',
  },
  infoRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  infoField: {
    flexGrow: 1,
    flexBasis: 140,
    minWidth: 140,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  description: {
    ...typography.input,
    minHeight: 96,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  // Text, member and row actions share one row; long text wraps and grows it.
  subtaskRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  subtaskInput: {
    ...typography.input,
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  subtaskIcon: { minHeight: 44, justifyContent: 'center' },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
});
